import { queryOptions, useQuery } from '@tanstack/react-query';
import type { ExtractionStatus } from '@vrzl/database';

import { apiFetch } from '@/domains/general/api/client';

import type { ValidationReport } from './types';

type ExtractionListItem = {
  id: string;
  caseId: string;
  status: ExtractionStatus;
  errors: unknown | null;
  requestedOn: string;
  completedOn: string | null;
};
type ExtractionListResponse = { items: ExtractionListItem[]; total: number };

const extractionQueries = {
  completedForCase: (caseId: string) =>
    queryOptions({
      queryKey: ['extractions', 'completed', caseId],
      queryFn: (): Promise<ExtractionListResponse> => {
        const params = new URLSearchParams();
        params.set('caseId', caseId);
        params.set('status', 'COMPLETED');
        params.set('limit', '1');
        return apiFetch(`/extractions?${params}`);
      },
      enabled: !!caseId,
    }),
  latestForCase: (caseId: string) =>
    queryOptions({
      queryKey: ['extractions', 'latest', caseId],
      queryFn: (): Promise<ExtractionListResponse> => {
        const params = new URLSearchParams();
        params.set('caseId', caseId);
        params.set('limit', '1');
        return apiFetch(`/extractions?${params}`);
      },
      enabled: !!caseId,
    }),
};

export function useCompletedExtraction(caseId: string) {
  return useQuery(extractionQueries.completedForCase(caseId));
}

export function useLatestExtraction(caseId: string) {
  return useQuery(extractionQueries.latestForCase(caseId));
}

const validationQueries = {
  report: (extractionId: string | null, criterionIds: readonly string[]) => {
    const sortedIds = [...criterionIds].sort();
    return queryOptions({
      queryKey: ['validation', extractionId, sortedIds],
      queryFn: (): Promise<ValidationReport> =>
        apiFetch<ValidationReport>('/criteria/validate', {
          method: 'POST',
          body: JSON.stringify({ extractionId, criterionIds: sortedIds }),
        }),
      enabled: !!extractionId && sortedIds.length > 0,
    });
  },
};

export function useValidationReport(
  extractionId: string | null,
  criterionIds: readonly string[],
) {
  return useQuery(validationQueries.report(extractionId, criterionIds));
}
